/* Dogwood, Sep 26 2026: branded pickers on app-rendered product form
   fields (Magical Form Fields on the Bellingham bucket products).
   The app draws plain <select> and <input type="date"> controls after
   the page loads, so their open lists and calendars are the operating
   system's. This watches the product details column and wraps
     - every <select> in the JC Select markup (assets/jc-select.js),
     - every date input in a JC Date host (assets/jc-date-picker.js),
   so they match the variant picker and the collection filters. The
   app's own element stays in place inside the wrapper, keeps its id,
   name, listeners and required flag, so submission and the app's
   logic are untouched. Browser validation on a hidden control becomes
   an inline message under the branded trigger instead of a bubble.
   Load after jc-select.js and jc-date-picker.js in layout/theme.liquid.
   Without JavaScript the native fields show as usual. */
(function () {
  var SCOPE = '.product-details .shopify-app-block';
  var CARET = '<svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">'
    + '<path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1)" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var counter = 0;

  function esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function labelFor(input) {
    var lbl = input.id ? document.querySelector('label[for="' + CSS.escape(input.id) + '"]') : null;
    if (!lbl) lbl = input.closest('label');
    var t = lbl ? lbl.textContent.replace(/\*/g, '').trim() : '';
    return t || input.getAttribute('aria-label') || input.name || '';
  }

  /* A required control hidden by the wrapper cannot show the browser's
     bubble sensibly, so cancel it and show the host's own message. */
  function brandValidation(native, host, trigger, show) {
    native.addEventListener('invalid', function (e) {
      e.preventDefault();
      show(native.validationMessage || 'Please choose an option');
      var form = native.form;
      if (trigger && (!form || form.querySelector(':invalid') === native)) trigger.focus();
    });
    native.addEventListener('focus', function () { if (trigger) trigger.focus(); });
  }

  /* ---- selects -> JC Select ---- */

  function isPlaceholder(opt, index) {
    if (opt.value === '') return true;
    return index === 0 && /^(select|choose|please|pick|--|—)/i.test(opt.text.trim());
  }

  function buildList(host, sel, id) {
    var list = host.querySelector('.jc-select__list');
    if (!list) return;
    var html = '';
    Array.prototype.forEach.call(sel.options, function (opt, i) {
      if (isPlaceholder(opt, i)) return;
      html += '<li class="jc-select__option" role="option" id="' + id + '-opt-' + (i + 1) + '"'
        + ' data-value="' + esc(opt.value) + '"'
        + ' aria-selected="' + (opt.selected ? 'true' : 'false') + '"'
        + (opt.disabled ? ' data-unavailable' : '')
        + '>' + esc(opt.text.trim()) + '</li>';
    });
    if (list.innerHTML !== html) list.innerHTML = html;
  }

  function wrapSelect(sel) {
    if (sel.dataset.jcWrapped || sel.multiple || sel.closest('jc-select')) return;
    if (!customElements.get('jc-select')) return;
    sel.dataset.jcWrapped = '1';
    var id = sel.id || ('jc-app-select-' + (++counter));
    var label = labelFor(sel);
    var first = sel.options[0];
    var ph = first && isPlaceholder(first, 0) ? first.text.trim() : 'Select one';

    var host = document.createElement('jc-select');
    host.className = 'jc-select jc-select--app';
    host.dataset.requiredMessage = 'Please choose an option';

    var marker = document.createComment('jc-select');
    sel.parentNode.insertBefore(marker, sel);
    host.appendChild(sel);
    sel.classList.add('jc-select__native');
    if (!sel.getAttribute('aria-label') && label) sel.setAttribute('aria-label', label);

    host.insertAdjacentHTML('beforeend',
      '<button type="button" class="jc-select__trigger" id="' + id + '-trigger" role="combobox"'
      + ' aria-haspopup="listbox" aria-expanded="false" aria-controls="' + id + '-list"'
      + (label ? ' aria-label="' + esc(label) + '"' : '') + '>'
      + '<span class="jc-select__value">' + esc(ph) + '</span>'
      + '<span class="jc-select__icon" aria-hidden="true">' + CARET + '</span>'
      + '</button>'
      + '<ul class="jc-select__list" id="' + id + '-list" role="listbox"'
      + (label ? ' aria-label="' + esc(label) + '"' : '') + ' tabindex="-1" hidden></ul>'
      + '<span class="jc-select__error" role="alert" hidden></span>');
    buildList(host, sel, id);

    marker.parentNode.replaceChild(host, marker);

    /* If the app rewrites the options (conditional logic), rebuild the list. */
    new MutationObserver(function () { buildList(host, sel, id); })
      .observe(sel, { childList: true, subtree: true, characterData: true, attributes: true });

    brandValidation(sel, host, host.querySelector('.jc-select__trigger'), function (msg) {
      if (typeof host.showError === 'function') host.showError(msg);
    });
  }

  /* ---- date inputs -> JC Date ---- */

  function wrapDate(input) {
    if (input.dataset.jcWrapped || input.closest('jc-date')) return;
    if (!customElements.get('jc-date')) return;
    input.dataset.jcWrapped = '1';

    var host = document.createElement('jc-date');
    host.className = 'jc-date jc-date--app';
    var marker = document.createComment('jc-date');
    input.parentNode.insertBefore(marker, input);
    host.appendChild(input);
    input.classList.add('jc-date__native');
    marker.parentNode.replaceChild(host, marker);
    /* jc-date handles invalid and focus itself; nothing more to wire. */
  }

  /* ---- watch the product column ---- */

  /* Checks the node itself as well as its descendants: the app may add a
     bare <select> or <input> straight into its block. */
  function apply(root) {
    var scope = root && root.querySelectorAll ? root : document;
    if (scope !== document && scope.matches) {
      if (scope.matches(SCOPE + ' select')) wrapSelect(scope);
      if (scope.matches(SCOPE + ' input[type="date"]')) wrapDate(scope);
    }
    scope.querySelectorAll(SCOPE + ' select').forEach(wrapSelect);
    scope.querySelectorAll(SCOPE + ' input[type="date"]').forEach(wrapDate);
  }

  function start() {
    if (!document.querySelector('.product-details')) return;
    apply(document);
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1) apply(added[j]);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
